import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { SubmitButton } from '@/components/submit-button';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { FormProvider } from '@/context/FormProvider';

import { useUpdateBatchCallRecipientMutation } from '@/query/scheduler.query';

import { type RecipientUpdateFormValues, recipientUpdateSchema } from '@/schemas/batch-call.schema';

import { useFormikError } from '@/lib/error';

import type { BatchRecipient } from '@/types/scheduler.type';

type Props = {
  batchCallId: number;
  recipient: BatchRecipient;
  open: boolean;
  onClose: () => void;
};

function RecipientUpdateForm({ onClose, isPending }: { onClose: () => void; isPending: boolean }) {
  const { getFieldProps } = useFormikContext<RecipientUpdateFormValues>();
  const hasError = useFormikError();

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <div className="space-y-2">
          <Field aria-invalid={hasError('name')}>
            <RequiredLabel htmlFor="name" className="text-sm font-medium">
              Full name
            </RequiredLabel>
            <Input
              id="name"
              placeholder="Enter recipient name"
              {...getFieldProps('name')}
              aria-invalid={hasError('name')}
            />
            <ErrorField name="name" />
          </Field>
        </div>

        <div className="space-y-2">
          <Field aria-invalid={hasError('phoneNumber')}>
            <RequiredLabel htmlFor="phoneNumber" className="text-sm font-medium">
              Phone number
            </RequiredLabel>
            <Input
              id="phoneNumber"
              placeholder="+1 234 567 8900"
              {...getFieldProps('phoneNumber')}
              aria-invalid={hasError('phoneNumber')}
            />
            <ErrorField name="phoneNumber" />
          </Field>
        </div>
      </div>

      <SheetFooter className="sticky bottom-0 bg-background border-t p-4 w-full flex flex-row gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={isPending}
          className="flex-1"
        >
          Cancel
        </Button>
        <SubmitButton isPending={isPending} className="flex-1" />
      </SheetFooter>
    </>
  );
}

export function RecipientUpdateSheet({ batchCallId, recipient, open, onClose }: Props) {
  const { mutate: updateRecipient, isPending } = useUpdateBatchCallRecipientMutation(batchCallId, {
    onSuccess: onClose,
  });

  const handleSubmit = (values: RecipientUpdateFormValues) => {
    updateRecipient({ recipientId: recipient.id, values });
  };

  const initialValues: RecipientUpdateFormValues = {
    name: recipient.name,
    phoneNumber: recipient.phoneNumber,
  };

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle>Edit Recipient</SheetTitle>
        </SheetHeader>

        <FormProvider
          initialValues={initialValues}
          validationSchema={recipientUpdateSchema}
          onSubmit={handleSubmit}
        >
          <RecipientUpdateForm onClose={onClose} isPending={isPending} />
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
}
